import React, { useEffect, useRef, useState } from 'react';
import Comment from './Comment';
import { usePatient } from '@ohif/ui';
import { addComment, getComments } from '../../utils/apiComments';

interface AddCommentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  uiNotificationService?: any;
}

function AddCommentDrawer({
  isOpen = false,
  onClose,
  uiNotificationService,
}: AddCommentDrawerProps) {
  const [comment, setComment] = useState('');
  const [commentsList, setCommentsList] = useState<any[]>([]);
  const [tags, setTags] = useState<string[]>([]);

  const drawerRef = useRef<HTMLDivElement>(null);

  const { alias, patient, project } = usePatient();

  const current_user_alias = alias || 'test_alias';

  useEffect(() => {
    const fetchComments = async () => {
      if (!isOpen) return;

      try {
        const response = await getComments(project, patient, 'CT');

        if (response?.error === 'UNAUTHORIZED') {
          console.error('Unauthorized');
          return;
        }

        const comments = Array.isArray(response) ? response : [];

        const sortedComments = comments
          .map((c: any) => ({
            id: c._id,
            userAlias: c.alias,
            timestamp: new Date(c.timestamp),
            text: c.text,
            tags: c.tags || [],
          }))
          .sort((a: any, b: any) => a.timestamp.getTime() - b.timestamp.getTime());

        setCommentsList(sortedComments);
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    };

    fetchComments();
  }, [project, patient, isOpen]);

  // Close drawer when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    // const handleClickOutside = (event: MouseEvent) => {
    //   if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
    //     onClose();
    //   }
    // };

    // document.addEventListener('mousedown', handleClickOutside);

    // return () => {
    //   document.removeEventListener('mousedown', handleClickOutside);
    // };
  }, [isOpen, onClose]);

  const handleSend = async () => {
    if (comment.trim() === '' || tags.length === 0) return;

    try {
      const payload = {
        alias: current_user_alias,
        text: comment,
        tags,
        timestamp: new Date().toISOString(),
      };

      const response = await addComment(project, patient, 'CT', payload);

      if (response?.error === 'UNAUTHORIZED') {
        uiNotificationService?.show({
          title: 'Comments',
          message: 'Unauthorized while adding comment.',
          type: 'error',
          duration: 3000,
        });
        return;
      }

      const formattedComment = {
        id: response._id,
        userAlias: response.alias,
        timestamp: new Date(response.timestamp),
        text: response.text,
        tags: response.tags || [],
      };

      setCommentsList(prev => [...prev, formattedComment]);
      setComment('');
      setTags([]);

      uiNotificationService?.show({
        title: 'Comments',
        message: 'Comment added successfully.',
        type: 'success',
        duration: 2500,
      });
    } catch (error) {
      console.error('Error adding comment:', error);

      uiNotificationService?.show({
        title: 'Comments',
        message: 'Could not add comment.',
        type: 'error',
        duration: 3000,
      });
    }
  };

  const handleCommentDeleted = (deletedCommentId: string) => {
    setCommentsList(prev => prev.filter(c => c.id !== deletedCommentId));
  };

  const handleTagChange = (tag: string, checked: boolean) => {
    let newTags = [...tags];

    if (checked) {
      newTags.push(tag);
    } else {
      newTags = newTags.filter(t => t !== tag);
    }

    setTags(newTags);
  };

  const tagOptions = ['Measurements', 'Segmentation', 'Saved configurations', 'All'];

  const isSendDisabled = tags.length === 0 || comment.trim() === '';

  return (
    <aside
      ref={drawerRef}
      className={`absolute right-2 top-4 bottom-2 z-[40]
      flex w-[400px] flex-col
      overflow-hidden
      rounded-[12px] border-2 bg-[#eeeeee]
      p-4 text-black
      transition-transform duration-300
      ${isOpen ? 'translate-x-0' : 'translate-x-[420px]'}`}
    >
      <div
        className="pointer-events-none absolute left-0 top-0 h-full w-4 -translate-x-full"
        style={{
          background: 'linear-gradient(to left, rgba(250,235,235,0.3), transparent)',
        }}
      />

      <div className="mb-2 flex justify-end">
        <button
          type="button"
          className="rounded p-1 text-gray-500 transition-colors hover:text-black"
          onClick={onClose}
          data-cy="add-comment-drawer-close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="black"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent flex-1 overflow-y-auto pr-1">
        {commentsList.map(c => {
          const current_user_comment = c.userAlias === current_user_alias;

          return (
            <Comment
              key={c.id}
              projectId={project}
              patientId={patient}
              commentId={c.id}
              userAlias={current_user_comment ? current_user_alias : c.userAlias}
              timestamp={c.timestamp}
              text={c.text}
              tags={c.tags}
              isCurrentUserComment={current_user_comment}
              onCommentDeleted={handleCommentDeleted}
              uiNotificationService={uiNotificationService}
            />
          );
        })}
      </div>

      <div className="mt-2 border-t border-[#b18aba] pt-3">
          <p className="mb-2 text-sm font-semibold text-gray-900">
            Sections <span className="text-red-500">*</span>
          </p>

          <div className="mb-3 flex flex-wrap gap-4">
            {tagOptions.map(tag => (
              <label
                key={tag}
                className="flex cursor-pointer select-none items-center gap-2 text-sm text-gray-800"
              >
                <input
                  type="checkbox"
                  checked={tags.includes(tag)}
                  onChange={e => handleTagChange(tag, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-400 accent-[#b18aba]"
                />
                {tag}
              </label>
            ))}
          </div>

        <div className="relative">
          <textarea
            placeholder="Add a comment..."
            value={comment}
            rows={3}
            onChange={e => setComment(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            className="w-full resize-none rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm text-black placeholder-gray-600 shadow-sm focus:border-[#b18aba] focus:outline-none"
          />

          <button
            type="button"
            onClick={handleSend}
            disabled={isSendDisabled}
            className={`absolute bottom-4 right-2 p-1 transition-colors ${
              isSendDisabled
                ? 'cursor-not-allowed text-gray-400'
                : 'text-[#b18aba] hover:text-[#9f73ad] active:text-[#8f5ea2]'
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 rotate-90"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
}

export default AddCommentDrawer;
